import {
  Node,
  mergeAttributes,
  nodeInputRule,
  nodePasteRule,
} from "@tiptap/core";

export type NotificationTagOption = {
  value: string;
  label?: string;
};

export const getNotificationTagDisplayValue = (value: string) => {
  const match = value.match(/^\{(.+)\}$/);
  return match ? match[1] : value;
};

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const buildTagPattern = (tags: NotificationTagOption[]) => {
  const pattern = tags
    .map((tag) => tag.value)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
    .map((value) => escapeRegExp(value))
    .join("|");

  return pattern ? `(${pattern})` : null;
};

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    notificationTag: {
      insertNotificationTag: (value: string) => ReturnType;
    };
  }
}

export const NotificationTag = Node.create<{
  tags: NotificationTagOption[];
}>({
  name: "notificationTag",
  inline: true,
  group: "inline",
  atom: true,
  selectable: true,

  addOptions() {
    return {
      tags: [],
    };
  },

  addAttributes() {
    return {
      value: {
        default: "",
        parseHTML: (element: HTMLElement) =>
          element.getAttribute("data-value") ?? element.textContent ?? "",
        renderHTML: (attributes) => ({
          "data-value": attributes.value,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "span[data-notification-tag]",
        getAttrs: (element) => {
          if (typeof element === "string") return false;
          const value = element.getAttribute("data-value");
          return value ? { value } : false;
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const value = HTMLAttributes["data-value"] ?? "";
    const tag = this.options.tags.find((tag) => tag.value === value);
    const displayValue = tag?.label ?? getNotificationTagDisplayValue(value);
    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        "data-notification-tag": "true",
        contenteditable: "false",
      }),
      displayValue,
    ];
  },

  addCommands() {
    return {
      insertNotificationTag:
        (value: string) =>
        ({ chain, tr }) => {
          if (!this.options.tags.some((tag) => tag.value === value)) {
            return false;
          }

          // Check if the current paragraph is empty or will only contain the tag
          const { $from } = tr.selection;
          const parentNode = $from.parent;
          const isEmptyParagraph =
            parentNode.type.name === "paragraph" &&
            parentNode.content.size === 0;
          const willBeOnlyTag =
            parentNode.type.name === "paragraph" &&
            parentNode.textContent.trim().length === 0;

          // Insert the tag and add a space after if needed for cursor positioning
          const chainCommands = chain().focus().insertContent({
            type: this.name,
            attrs: { value },
          });

          // Add a space after the tag if it's the only content in the paragraph
          if (isEmptyParagraph || willBeOnlyTag) {
            chainCommands.insertContent(" ");
          }

          return chainCommands.run();
        },
    };
  },

  addInputRules() {
    const pattern = buildTagPattern(this.options.tags);
    if (!pattern) return [];

    return [
      nodeInputRule({
        find: new RegExp(`${pattern}$`),
        type: this.type,
        getAttributes: (match) => ({ value: match[1] }),
      }),
    ];
  },

  addPasteRules() {
    const pattern = buildTagPattern(this.options.tags);
    if (!pattern) return [];

    return [
      nodePasteRule({
        find: new RegExp(pattern, "g"),
        type: this.type,
        getAttributes: (match) => ({ value: match[1] }),
      }),
    ];
  },

  addNodeView() {
    const resolveDisplayValue = (value: string) => {
      const tag = this.options.tags.find((tag) => tag.value === value);
      return tag?.label ?? getNotificationTagDisplayValue(value);
    };

    return ({ editor, node, getPos }) => {
      const button = document.createElement("button");
      button.type = "button";
      button.contentEditable = "false";
      button.dataset.value = node.attrs.value;
      button.setAttribute("data-notification-tag", "true");
      button.className =
        "notification-tag-pill inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary transition-colors hover:bg-primary/20";

      const label = document.createElement("span");
      label.textContent = resolveDisplayValue(node.attrs.value);
      label.className = "font-mono";

      const remove = document.createElement("span");
      remove.textContent = "×";
      remove.className = "text-base leading-none text-primary";

      button.append(label, remove);

      const handleRemove = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        const position =
          typeof getPos === "function" ? getPos() : (getPos as number);
        if (typeof position !== "number") return;

        editor
          .chain()
          .focus()
          .setTextSelection({
            from: position,
            to: position + node.nodeSize,
          })
          .deleteSelection()
          .run();
      };

      button.addEventListener("click", handleRemove);

      return {
        dom: button,
        destroy() {
          button.removeEventListener("click", handleRemove);
        },
        update: (updatedNode) => {
          if (updatedNode.type !== node.type) {
            return false;
          }
          if (updatedNode.attrs.value !== node.attrs.value) {
            label.textContent = resolveDisplayValue(updatedNode.attrs.value);
            button.dataset.value = updatedNode.attrs.value;
          }
          return true;
        },
        ignoreMutation: () => true,
      };
    };
  },
});
